import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import DataTable from '../DataTable/DataTable';
import { usePagination } from '../../hooks/usePagination';
import { useSorting } from '../../hooks/useSorting';
import FilterSection, { UnifiedFilter } from './FilterSection';
import { convertUnifiedFiltersToLegacyFormat } from './filterUtils';
import { cn } from '../../lib/utils';
import ConfirmationDialog from '../ui/ConfirmationDialog';
import TablePresets from './TablePresets';
import ConditionalFormattingManager from './ConditionalFormattingManager';
import useSWR from 'swr';
import { getTablePresets, getDefaultTablePreset, TablePreset } from '../../services/tablePresetsService';
import { getConditionalFormattingRules } from '../../services/conditionalFormattingService';
import { useApiOptions } from '../../hooks/useApiOptions';
import { ChevronDownIcon, CheckIcon, XIcon, PaintBrushIcon } from '../ui/Icons';
import { useNotification } from '../../contexts/NotificationContext';
import { APP_CONFIG } from '../../config/appConfig';
import { useSettings } from '../../contexts/SettingsContext';
import { QuickFilterButtons } from '../FiltersPanel';
import { getFilterPresets, FilterPreset } from '../../services/filterPresetsService';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  getListStateStorageKey,
  loadPersistedGenericListState,
  persistGenericListState,
} from '../../lib/listStatePersistence';

interface GenericListPageProps<T> {
  /** Optional callbacks passed to getColumns (e.g. onOpenLinkedTasksPanel for packages) */
  listContext?: Record<string, (item: T) => void>;
  // Data hook that returns { data/clients, isLoading, mutate }
  useData: (searchParams?: string) => {
    data?: T[];
    clients?: T[];
    isLoading: boolean;
    mutate: () => void | any;
  };
  // Module configuration
  config: {
    module: {
      title: string;
      /** Permission module key for can(moduleKey, 'delete') – when set, delete button is hidden when user has no delete permission. */
      permissionModuleKey?: string;
      route: {
        list: string;
        new: string;
        detail: (id: number) => string;
        edit: (id: number) => string;
      };
    };
    pagination: {
      initialItemsPerPage: number;
      itemsPerPageOptions: number[];
    };
    search: {
      placeholder: string;
    };
    filterGroups?: Array<{
      name: string;
      label: string;
      icon?: string | React.ReactNode;
      fields: string[];
    }>;
    filters: {
      fields: Array<{
        key: string;
        label: string;
        placeholder?: string;
        type?: 'text' | 'date' | 'number' | 'boolean' | 'select';
        getValue?: (item: T) => any;
        options?: any[];
        apiEndpoint?: string; // API endpoint to fetch options for foreign key/select fields
        labelField?: string; // Field name in API response to use as label (default: 'name', 'as_descr', etc.)
      }>;
      applyFilter: (
        items: T[],
        filters: any,
        dateRanges?: Record<string, { start: Date | null; end: Date | null }>
      ) => T[];
      /** Optional: filter items by the main search box text (client-side). */
      applySearch?: (items: T[], searchTerm: string) => T[];
    };
    fields: Array<{
      name: string;
      label: string;
      type?: string;
      options?: any[];
      apiEndpoint?: string; // API endpoint to fetch options for foreign key/select fields
      labelField?: string; // Field name in API response to use as label
    }>;
    toolbar: {
      actions: Array<{
        key: string;
        icon: string;
        title: string;
        variant: 'default' | 'primary';
        onClick?: () => void;
        href?: string;
        showTitle?: boolean;
        className?: string;
        /** When 'beforeTablePresets', this action is rendered before the table preset button */
        position?: 'default' | 'beforeTablePresets';
        dropdown?: Array<{
          key: string;
          label: string;
          icon?: string;
          onClick: (exportData?: {
            sortedData: any[];
            tableColumns: any[];
            entityName: string;
            exportFileName: string;
            formatDate?: (dateString?: string) => string;
          }) => void | Promise<void>;
        }>;
      }>;
      customButton?: {
        visible?: boolean;
        icon: string;
        title?: string;
        onClick?: () => void | Promise<void>;
        href?: string;
        variant?: 'default' | 'primary';
      };
      viewToggle?: {
        enabled: boolean;
        views: Array<{
          key: string;
          label: string;
          icon: string;
          href: string;
        }>;
      };
    };
    table: {
      actionsPosition: 'left' | 'right';
      /** When true, actions are rendered inside columns (e.g. two columns) and the DataTable actions prop is not used */
      actionsInColumns?: boolean;
      onRowClick?: (item: T) => void;
      keyExtractor: (item: T) => number | string;
      /** When set, a "Client summary" icon button is added to each row that has a clientId (links to /clients/:id/summary) */
      getClientId?: (item: T) => number | null | undefined;
    };
    defaultTableDensity?: 'compact' | 'normal' | 'comfortable';
    /** Default sort when no sort is applied (e.g. { key: 'serviceEndDate', direction: 'asc' }). */
    defaultSort?: { key: string; direction: 'asc' | 'desc' };
    emptyStates: {
      noData: string;
      noSearchResults: string;
      noFilterResults: string;
    };
    delete: {
      title: string;
      message: string;
      confirmText: string;
      cancelText: string;
      errorMessage: string;
      handler: (id: number) => Promise<void>;
    };
    utils: {
      formatDate: (dateString?: string) => string;
    };
    getColumns: (formatDate: (dateString?: string) => string, ...args: any[]) => any[];
    getToolbarIcon: (iconName: string) => React.ReactNode;
    getTableActions: (
      item: T,
      onDelete: (id: number) => void,
      ...rest: any[]
    ) => React.ReactNode;
  };
  /** Merged into unified filter state once on mount (e.g. from URL query on list pages). */
  initialUnifiedFilters?: Record<string, UnifiedFilter>;
}

export type TableActionsOptions = { canDelete?: boolean; canEdit?: boolean };

function GenericListPage<T extends { [key: string]: any }>({
  useData,
  config,
  listContext,
  initialUnifiedFilters,
}: GenericListPageProps<T>) {
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const { appConfig } = useSettings();
  const { colors, theme } = useTheme();
  const { can } = useAuth();
  const permissionModuleKey = config.module.permissionModuleKey;
  const canDelete = !permissionModuleKey || can(permissionModuleKey, 'delete');
  const canEdit = !permissionModuleKey || can(permissionModuleKey, 'edit');
  const tableActionsOptions: TableActionsOptions = { canDelete, canEdit };
  const isCompact = false;
  const enableAdvancedSearch = appConfig.features.enableAdvancedSearch && !isCompact;
  const enableExport = appConfig.features.enableExport && !isCompact;
  const enableTablePresets = appConfig.features.enableTablePresets && !isCompact;
  const enableConditionalFormatting = appConfig.features.enableConditionalFormatting && !isCompact;

  const defaultFilterTab = config.filterGroups?.[0]?.name || 'basic';
  const listStateStorageKey = getListStateStorageKey(config.module.route.list);
  const persistedList = loadPersistedGenericListState(listStateStorageKey, defaultFilterTab);

  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(() => persistedList?.searchTerm ?? '');
  const [filtersVisible, setFiltersVisible] = useState(() => persistedList?.filtersVisible ?? false);
  const [activeFilterTab, setActiveFilterTab] = useState<string>(
    () => persistedList?.activeFilterTab ?? defaultFilterTab
  );
  const defaultTableDensity = config.defaultTableDensity || appConfig.table.defaultDensity;
  const [tableDensity, setTableDensity] = useState<'compact' | 'normal' | 'comfortable'>(defaultTableDensity);

  // Unified filter state
  const [unifiedFilters, setUnifiedFilters] = useState<Record<string, UnifiedFilter>>(
    () => persistedList?.unifiedFilters ?? {}
  );

  const urlFiltersMergedRef = useRef(false);
  useEffect(() => {
    if (urlFiltersMergedRef.current || !initialUnifiedFilters || Object.keys(initialUnifiedFilters).length === 0) {
      return;
    }
    setUnifiedFilters((prev) => ({ ...prev, ...initialUnifiedFilters }));
    urlFiltersMergedRef.current = true;
  }, [initialUnifiedFilters]);

  // Table preset state
  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>({});
  const [columnWidths, setColumnWidths] = useState<Record<string, string>>({});
  const [columnOrder, setColumnOrder] = useState<string[]>([]);
  const [columnLabels, setColumnLabels] = useState<Record<string, string>>({});
  const [footerConfig, setFooterConfig] = useState<{
    showFooter?: boolean;
    useFilteredData?: boolean;
    footerColumns?: Record<string, {
      footerType: 'sum' | 'avg' | 'count' | 'custom';
      footerLabel?: string;
    }>;
  }>({ showFooter: false, useFilteredData: true, footerColumns: {} });

  // Non-persistent UI state
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [showTablePresets, setShowTablePresets] = useState(false);
  const [tablePresetsPanelWidth, setTablePresetsPanelWidth] = useState(420);
  const [tablePresetsResizing, setTablePresetsResizing] = useState(false);
  const tablePresetsResizeStartRef = useRef({ x: 0, width: 0 });
  const [lastAppliedPreset, setLastAppliedPreset] = useState<TablePreset | null>(null);
  const [tablePresetsDropdownOpen, setTablePresetsDropdownOpen] = useState(false);
  const tablePresetsDropdownRef = useRef<HTMLDivElement>(null);
  const tablePresetsButtonRef = useRef<HTMLButtonElement>(null);
  const tablePresetsMenuRef = useRef<HTMLDivElement>(null);
  const [tablePresetsDropdownPosition, setTablePresetsDropdownPosition] = useState<{ top: number; right: number } | null>(null);
  const [downloadDropdownOpen, setDownloadDropdownOpen] = useState(false);
  const downloadDropdownRef = useRef<HTMLDivElement>(null);
  const downloadButtonRef = useRef<HTMLButtonElement>(null);
  const downloadMenuRef = useRef<HTMLDivElement>(null);
  const [downloadDropdownPosition, setDownloadDropdownPosition] = useState<{ top: number; right: number } | null>(null);
  const [viewToggleDropdownOpen, setViewToggleDropdownOpen] = useState(false);
  const viewToggleDropdownRef = useRef<HTMLDivElement>(null);
  const viewToggleButtonRef = useRef<HTMLButtonElement>(null);
  const viewToggleMenuRef = useRef<HTMLDivElement>(null);
  const [viewToggleDropdownPosition, setViewToggleDropdownPosition] = useState<{ top: number; right: number } | null>(null);
  const [showConditionalFormatting, setShowConditionalFormatting] = useState(false);
  const conditionalFormattingRef = useRef<HTMLDivElement>(null);
  const conditionalFormattingButtonRef = useRef<HTMLButtonElement>(null);
  const [conditionalFormattingPosition, setConditionalFormattingPosition] = useState<{ top: number; right: number } | null>(null);
  
  const tableId = config.module.title.toLowerCase().replace(/\s+/g, '-');

  const dataHookResult = useData(searchTerm || undefined);
  const data = (dataHookResult.data || dataHookResult.clients || []) as T[];
  const isLoading = dataHookResult.isLoading;
  const mutate = dataHookResult.mutate;

  // Convert unified filters to legacy format and apply filters
  const filteredData = useMemo(() => {
    // Get all filter keys from config to ensure all are initialized
    const allFilterKeys = config.filters.fields.map((f) => f.key);
    // Identify text filter keys (those that use string instead of array)
    const textFilterKeys = config.filters.fields
      .filter((f) => f.type === 'text')
      .map((f) => f.key);

    const { filters, dateRanges } = convertUnifiedFiltersToLegacyFormat(
      unifiedFilters,
      allFilterKeys,
      textFilterKeys
    );
    let result = config.filters.applyFilter(data, filters as any, dateRanges);
    if (config.filters.applySearch && searchTerm?.trim()) {
      result = config.filters.applySearch(result, searchTerm.trim());
    }
    return result;
  }, [data, unifiedFilters, config.filters, searchTerm]);

  // Sorting (use config.defaultSort so list can start sorted by e.g. end date ascending)
  const { sortConfig, handleSort, setSortConfig, sortedData } = useSorting<T>(config.defaultSort);

  // Apply sorting
  const sortedItems = useMemo(
    () => sortedData(filteredData),
    [filteredData, sortedData]
  );

  // Pagination - use settings if available, otherwise use config
  const { paginationProps, paginatedData } = usePagination({
    totalItems: sortedItems.length,
    initialItemsPerPage: appConfig.table.defaultPageSize, // Use settings value instead of config
  });

  // Apply pagination
  const paginatedItems = useMemo(
    () => paginatedData(sortedItems),
    [sortedItems, paginatedData]
  );

  const handleDelete = async (id: number) => {
    try {
      await config.delete.handler(id);
      mutate();
      setDeleteConfirm(null);
      showSuccess('Item deleted successfully');
    } catch (error: any) {
      console.error('Failed to delete:', error);
      const errorMessage = error?.response?.data?.message || error?.message || config.delete.errorMessage;
      showError(errorMessage);
    }
  };

  // Get all columns from config using config's formatDate
  const allColumns = useMemo(
    () => {
      const onDelete = (id: number) => setDeleteConfirm(id);
      const contextWithMutate = { ...listContext, mutate, onDelete, tableActionsOptions };
      // Try to pass colors and listContext (for functions that accept them)
      try {
        const result = (config.getColumns as any)(config.utils.formatDate, undefined, colors, contextWithMutate);
        if (result && Array.isArray(result) && result.length > 0) return result;
      } catch {}
      try {
        const result = (config.getColumns as any)(config.utils.formatDate, undefined, colors);
        if (result && Array.isArray(result) && result.length > 0) return result;
      } catch {}
      try {
        const result = (config.getColumns as any)(config.utils.formatDate, colors);
        if (result && Array.isArray(result) && result.length > 0) return result;
      } catch {}
      // Fallback to original call
      return config.getColumns(config.utils.formatDate);
    },
    [config, colors, listContext, mutate, tableActionsOptions]
  );

  // Collect all API endpoints from filter fields and form fields that need to fetch options
  // Priority: form fields (config.fields) first, then filter fields (config.filters.fields)
  const apiEndpoints = useMemo(() => {
    const endpoints: Record<string, { endpoint: string; labelField?: string }> = {};
    
    // First, collect from form fields (config.fields)
    config.fields.forEach(field => {
      if (field.apiEndpoint) {
        endpoints[field.name] = {
          endpoint: field.apiEndpoint,
          labelField: field.labelField,
        };
      }
    });
    
    // Then, collect from filter fields (config.filters.fields) as fallback
    // Only add if not already present (form fields take priority)
    config.filters.fields.forEach(field => {
      if (field.apiEndpoint && !endpoints[field.key]) {
        endpoints[field.key] = {
          endpoint: field.apiEndpoint,
          labelField: field.labelField,
        };
      }
    });
    
    return endpoints;
  }, [config.filters.fields, config.fields]);

  // Fetch options from API endpoints using custom hook
  const apiOptionsData = useApiOptions(apiEndpoints);

  // Get available columns for conditional formatting
  const availableColumns = useMemo(() => {
    return allColumns.map(col => {
      const columnKey = String(col.key);
      
      // Check if this is a foreign key column
      // Foreign keys typically:
      // 1. End with 'Id' (e.g., 'clientId', 'invoiceTypeId')
      // 2. Reference related entities with dots (e.g., 'client.clientName')
      // 3. Are marked as select type in form fields
      const hasIdSuffix = columnKey.endsWith('Id');
      const hasNestedPath = columnKey.includes('.');
      
      // For nested paths like 'client.clientName', find the filter field by the base key (e.g., 'clientId')
      let filterFieldKey = columnKey;
      if (hasNestedPath) {
        const baseKey = columnKey.split('.')[0];
        filterFieldKey = `${baseKey}Id`;
      }
      
      // Find matching filter field to get type information and API config
      const filterField = config.filters.fields.find(f => f.key === filterFieldKey);
      
      // Find corresponding field in config.fields to check if it's a select type
      const formField = config.fields.find(f => {
        // Handle nested keys like 'client.clientName' -> check for 'clientId'
        if (hasNestedPath) {
          const baseKey = columnKey.split('.')[0];
          return f.name === `${baseKey}Id` || f.name === baseKey;
        }
        return f.name === columnKey || f.name === `${columnKey}Id`;
      });
      
      const isSelectField = formField?.type === 'select';
      const isForeignKey = hasIdSuffix || hasNestedPath || isSelectField;
      
      // Determine the foreign key field name for value extraction
      let foreignKeyField: string | undefined;
      if (hasNestedPath) {
        // For 'client.clientName', use 'clientId'
        foreignKeyField = `${columnKey.split('.')[0]}Id`;
      } else if (hasIdSuffix) {
        // For columns ending with 'Id', use the column key itself
        foreignKeyField = columnKey;
      } else if (formField) {
        // Use the form field name
        foreignKeyField = formField.name;
      }
      
      // Get options - use filterField options if available, otherwise extract from tableData or API for select fields
      let options = filterField?.options || [];
      
      // Get apiEndpoint and labelField from formField (config.fields) first, fallback to filterField
      const apiEndpoint = formField?.apiEndpoint || filterField?.apiEndpoint;
      const labelField = formField?.labelField || filterField?.labelField;
      
      // If we have an API endpoint, fetch options from API
      // Use filterFieldKey to get the correct API data (e.g., 'clientId' instead of 'client.clientName')
      if (apiEndpoint && apiOptionsData[filterFieldKey]) {
        const apiData = apiOptionsData[filterFieldKey]; // Use filterFieldKey, not columnKey
        const effectiveLabelField = labelField; // Use only the configured labelField
        
        options = apiData.map((item: any) => {
          // Use only the configured labelField, no fallback
          const label = effectiveLabelField && item[effectiveLabelField] !== undefined && item[effectiveLabelField] !== null
            ? item[effectiveLabelField]
            : `Item #${item.id}`;
          
          return {
            value: String(item.id),
            label: String(label),
          };
        }).sort((a: { value: string; label: string }, b: { value: string; label: string }) => a.label.localeCompare(b.label));
      }
      // If options are still empty but it's a select field, try to extract from tableData
      else if (options.length === 0 && (filterField?.type === 'select' || isSelectField) && data && data.length > 0) {
        const uniqueOptions = new Map<string, string>();
        
        data.forEach((row: any) => {
          // For status field specifically (clientAppStatus), use the description from related entity
          if (columnKey === 'clientAppStatus' && row.clientAppStatus !== null && row.clientAppStatus !== undefined) {
            const statusId = String(row.clientAppStatus);
            const statusLabel = row.clientAppStatusDescr?.as_descr || `Status #${statusId}`;
            if (!uniqueOptions.has(statusId)) {
              uniqueOptions.set(statusId, statusLabel);
            }
          } else if (filterField?.getValue) {
            // Use the getValue function from filterField to get the display value
            const displayValue = filterField.getValue(row);
            const rawValue = row[columnKey] ?? row[foreignKeyField || columnKey];
            if (rawValue !== null && rawValue !== undefined && displayValue) {
              const valueKey = String(rawValue);
              if (!uniqueOptions.has(valueKey)) {
                uniqueOptions.set(valueKey, String(displayValue));
              }
            }
          } else {
            // Fallback: use the raw value
            const rawValue = row[columnKey] ?? row[foreignKeyField || columnKey];
            if (rawValue !== null && rawValue !== undefined) {
              const valueKey = String(rawValue);
              if (!uniqueOptions.has(valueKey)) {
                uniqueOptions.set(valueKey, valueKey);
              }
            }
          }
        });
        
        // Convert map to options array
        options = Array.from(uniqueOptions.entries())
          .map(([value, label]) => ({ value, label }))
          .sort((a, b) => a.label.localeCompare(b.label));
      }
      
      return {
        key: columnKey,
        label: col.header,
        type: filterField?.type || (isSelectField ? 'select' : 'text'), // Use 'select' if form field is select
        options: options, // Include options for select/boolean types
        isForeignKey: isForeignKey,
        foreignKeyField: foreignKeyField, // The actual foreign key field name (e.g., 'clientId')
        apiEndpoint: apiEndpoint, // API endpoint for fetching options (from formField or filterField)
        labelField: labelField, // Field name in API response to use as label (from formField or filterField)
      };
    });
  }, [allColumns, config.filters.fields, config.fields, data, apiOptionsData]);

  const moduleNameForConditionalFormatting = tableId; // Use tableId as moduleName for conditional formatting

  // Load conditional formatting rules
  const { data: conditionalFormattingRules = [] } = useSWR(
    enableConditionalFormatting ? `conditional-formatting-${moduleNameForConditionalFormatting}` : null,
    enableConditionalFormatting ? () => getConditionalFormattingRules(moduleNameForConditionalFormatting) : null,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
    }
  );

  // Load filter presets to display as quick filters
  const { data: savedFilterPresets = [] } = useSWR(
    enableAdvancedSearch ? `filter-presets-${tableId}` : null,
    enableAdvancedSearch ? () => getFilterPresets(tableId) : null,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
    }
  );

  // Restore quick filter from URL when returning from add/edit (so the selected filter is preserved)
  const restoredPresetRef = useRef<string | null>(null);
  useEffect(() => {
    if (!enableAdvancedSearch || !savedFilterPresets?.length) return;
    const presetId = searchParams.get('preset');
    if (!presetId || presetId === 'all') {
      restoredPresetRef.current = null;
      return;
    }
    if (restoredPresetRef.current === presetId) return;
    const preset = savedFilterPresets.find((p: FilterPreset) => String(p.id) === presetId);
    if (preset?.filters && typeof preset.filters === 'object') {
      setUnifiedFilters({ ...(preset.filters as Record<string, UnifiedFilter>) });
      restoredPresetRef.current = presetId;
    }
  }, [enableAdvancedSearch, savedFilterPresets, searchParams]);

  // Keep search + filter UI in sessionStorage so navigating to edit/detail and back preserves them
  useEffect(() => {
    persistGenericListState(listStateStorageKey, {
      searchTerm,
      unifiedFilters,
      activeFilterTab,
      filtersVisible,
    });
  }, [listStateStorageKey, searchTerm, unifiedFilters, activeFilterTab, filtersVisible]);

  // Initialize visible columns on mount
  useEffect(() => {
    if (Object.keys(visibleColumns).length === 0 && allColumns.length > 0) {
      const initialVisibleColumns = allColumns.reduce(
        (acc, col) => ({
          ...acc,
          [String(col.key)]: true,
        }),
        {}
      );
      setVisibleColumns(initialVisibleColumns);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allColumns.length]);

  // Filter columns based on visibility and apply footer, sizing & order config
  const columns = useMemo(() => {
    let filtered = allColumns;
    if (Object.keys(visibleColumns).length > 0) {
      filtered = allColumns.filter((col) => (visibleColumns as Record<string, boolean>)[String(col.key)] !== false);
    }

    // Apply footer configuration, max-width, and custom column labels from presets
    let mapped = filtered.map((col) => {
      const columnKey = String(col.key);
      const footerColumnConfig = (footerConfig?.footerColumns as Record<string, any>)?.[columnKey];
      const maxWidthFromPreset = columnWidths[columnKey];
      const customLabel = columnLabels[columnKey];

      const updated: any = {
        ...col,
        header: customLabel || col.header,
      };

      if (footerColumnConfig) {
        updated.footerType = footerColumnConfig.footerType;
        updated.footerLabel = footerColumnConfig.footerLabel;
      }

      if (maxWidthFromPreset) {
        updated.maxWidth = maxWidthFromPreset;
      }

      return updated;
    });

    // Apply column order from presets (if any)
    if (columnOrder && columnOrder.length > 0) {
      const orderMap = new Map<string, number>();
      columnOrder.forEach((key, index) => {
        orderMap.set(String(key), index);
      });

      mapped = mapped
        .slice()
        .sort((a, b) => {
          const aKey = String(a.key);
          const bKey = String(b.key);
          const aIndex = orderMap.has(aKey) ? (orderMap.get(aKey) as number) : Number.MAX_SAFE_INTEGER;
          const bIndex = orderMap.has(bKey) ? (orderMap.get(bKey) as number) : Number.MAX_SAFE_INTEGER;
          if (aIndex !== bIndex) return aIndex - bIndex;
          // Fallback to original order when both are not in columnOrder
          const originalAIndex = allColumns.findIndex((col) => String(col.key) === aKey);
          const originalBIndex = allColumns.findIndex((col) => String(col.key) === bKey);
          return originalAIndex - originalBIndex;
        });
    }

    return mapped;
  }, [allColumns, visibleColumns, footerConfig, columnWidths, columnOrder, columnLabels]);

  // Load table presets from API using SWR
  const { data: savedTablePresets = [], mutate: mutateTablePresets } = useSWR(
    enableTablePresets ? `table-presets-${tableId}` : null,
    enableTablePresets ? () => getTablePresets(tableId) : null,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
    }
  );

  // Close table presets dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if any modal is open (TablePresets modal or ConfirmationDialog)
      const isModalOpen = document.querySelector('[role="dialog"][aria-modal="true"]');
      if (isModalOpen) {
        return; // Do not close if a modal is open
      }

      if (tablePresetsDropdownRef.current && 
          tablePresetsMenuRef.current &&
          !tablePresetsDropdownRef.current.contains(event.target as Node) &&
          !tablePresetsMenuRef.current.contains(event.target as Node)) {
        setTablePresetsDropdownOpen(false);
        setTablePresetsDropdownPosition(null);
      }
      if (downloadDropdownRef.current && 
          downloadMenuRef.current &&
          !downloadDropdownRef.current.contains(event.target as Node) &&
          !downloadMenuRef.current.contains(event.target as Node)) {
        setDownloadDropdownOpen(false);
        setDownloadDropdownPosition(null);
      }
      if (viewToggleDropdownRef.current && 
          viewToggleMenuRef.current &&
          !viewToggleDropdownRef.current.contains(event.target as Node) &&
          !viewToggleMenuRef.current.contains(event.target as Node)) {
        setViewToggleDropdownOpen(false);
        setViewToggleDropdownPosition(null);
      }
      if (conditionalFormattingRef.current && 
          !conditionalFormattingRef.current.contains(event.target as Node) &&
          !(event.target as HTMLElement).closest('[data-conditional-formatting-manager]')) {
        setShowConditionalFormatting(false);
        setConditionalFormattingPosition(null);
      }
    };
    if (tablePresetsDropdownOpen || downloadDropdownOpen || viewToggleDropdownOpen || showConditionalFormatting) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [tablePresetsDropdownOpen, downloadDropdownOpen, viewToggleDropdownOpen, showConditionalFormatting]);

  // Resize table presets panel (sizebar on left edge)
  useEffect(() => {
    if (!tablePresetsResizing) return;
    const minW = 320;
    const maxW = 720;
    const onMove = (e: MouseEvent) => {
      const start = tablePresetsResizeStartRef.current;
      const newW = start.width + start.x - e.clientX;
      setTablePresetsPanelWidth(Math.min(maxW, Math.max(minW, newW)));
    };
    const onUp = () => setTablePresetsResizing(false);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [tablePresetsResizing]);

  // Get current table configuration
  const getCurrentTableConfig = () => ({
    visibleColumns: visibleColumns,
    tableDensity: tableDensity,
    columnWidths: columnWidths,
    columnOrder: columnOrder || [],
    sortConfig: sortConfig
      ? [
          {
            key: sortConfig.key,
            direction: sortConfig.direction,
            priority: 1,
          },
        ]
      : [],
    footerConfig: footerConfig,
  });

  // Check if a preset is active
  const isTablePresetActive = (preset: any) => {
    return JSON.stringify(preset.config) === JSON.stringify(getCurrentTableConfig());
  };

  // Load default table preset on mount
  useEffect(() => {
    const loadDefaultPreset = async () => {
      try {
        const defaultPreset = await getDefaultTablePreset(tableId);
        if (defaultPreset) {
          handleLoadDefaultPreset(defaultPreset.config);
        }
      } catch (error) {
        console.warn('Failed to load default table preset:', error);
      }
    };
    if (enableTablePresets) {
      loadDefaultPreset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableId, enableTablePresets]);

  // Apply table preset
  const handleApplyTablePreset = (presetId: string | '') => {
    if (!presetId) {
      // Reset to default
      const defaultVisibleColumns = allColumns.reduce(
        (acc, col) => ({
          ...acc,
          [String(col.key)]: true,
        }),
        {}
      );
      setVisibleColumns(defaultVisibleColumns);
      setTableDensity(defaultTableDensity);
      setColumnWidths({});
      setColumnOrder([]);
      setColumnLabels({});
      setSortConfig(null);
      setFooterConfig({ showFooter: false, useFilteredData: true, footerColumns: {} });
      setTablePresetsDropdownOpen(false);
      setLastAppliedPreset(null);
      return;
    }
    const preset = savedTablePresets.find((p: any) => p.id.toString() === presetId) as TablePreset | undefined;
    if (preset) {
      handleLoadDefaultPreset(preset.config);
      setLastAppliedPreset(preset);
      setTablePresetsDropdownOpen(false);
    }
  };

  // Handle default preset loading
  const handleLoadDefaultPreset = (presetConfig: {
    visibleColumns: Record<string, boolean>;
    tableDensity: 'compact' | 'normal' | 'comfortable';
    columnWidths?: Record<string, string>;
    columnOrder?: string[];
    columnLabels?: Record<string, string>;
    sortConfig?: Array<{ key: string; direction: 'asc' | 'desc'; priority?: number }>;
    footerConfig?: {
      showFooter?: boolean;
      useFilteredData?: boolean;
      footerColumns?: Record<string, {
        footerType: 'sum' | 'avg' | 'count' | 'custom';
        footerLabel?: string;
      }>;
    };
  }) => {
    if (presetConfig.visibleColumns) {
      setVisibleColumns(presetConfig.visibleColumns);
    }
    if (presetConfig.tableDensity) {
      setTableDensity(presetConfig.tableDensity);
    }
    if (presetConfig.columnWidths) {
      setColumnWidths(presetConfig.columnWidths);
    }
    if (presetConfig.columnOrder) {
      setColumnOrder(presetConfig.columnOrder);
    }
    if (presetConfig.columnLabels !== undefined) {
      setColumnLabels(presetConfig.columnLabels);
    }
    if (presetConfig.sortConfig && presetConfig.sortConfig.length > 0) {
      const first = presetConfig.sortConfig[0];
      setSortConfig({ key: first.key, direction: first.direction });
    } else {
      setSortConfig(null);
    }
    if (presetConfig.footerConfig) {
      setFooterConfig(presetConfig.footerConfig);
    }
  };

  // Get FilterSection render output
  const filterSection = FilterSection({
    config: {
      filterFields: config.filters.fields.map(f => ({
        ...f,
        getValue: f.getValue || ((item: any) => String(item[f.key] || '')),
      })),
      formFields: config.fields.map((f) => ({
        name: f.name,
        label: f.label,
        type: f.type || 'text',
        options: f.options,
      })),
      filterGroups: config.filterGroups,
      applyFilter: config.filters.applyFilter,
    },
    entityName: config.module.title,
    data: data,
    apiOptionsData: apiOptionsData, // Pass API-fetched options to filters
    filtersVisible,
    setFiltersVisible,
    unifiedFilters,
    setUnifiedFilters,
    activeFilterTab,
    setActiveFilterTab,
    searchTerm,
    setSearchTerm,
    searchPlaceholder: config.search.placeholder,
    filteredCount: filteredData.length,
    enableAdvancedSearch,
  });

  return (
    <div className="flex h-full min-h-0">
      <div className="flex-1 min-w-0 flex flex-col min-h-0 overflow-hidden">
    <div className="flex flex-col h-full min-h-0">
      {/* Top Controls (Search + Filter Toggle) */}
      <div className="flex-shrink-0 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 mb-1">
        <div className="w-full sm:flex-1 min-w-0">
          {filterSection?.topControls}
        </div>
        {/* Toolbar Actions - grouped container with right padding on last button */}
        <div className={cn('flex items-center gap-3 flex-shrink-0 overflow-x-auto flex-nowrap rounded-xl border py-1.5 pl-3 pr-5', colors.border, colors.surface)}>
          {/* Custom Button - Configurable from module config */}
          {config.toolbar.customButton && config.toolbar.customButton.visible !== false && (canEdit || config.toolbar.customButton.href !== config.module.route.new) && (() => {
            const customButton = config.toolbar.customButton!;
            return (
              <div className="relative flex-shrink-0 inline-block">
                {customButton.href ? (
                  <Link
                    to={customButton.href}
                    className={cn(
                      'inline-flex items-center justify-center h-9 w-9 rounded-lg border transition-colors',
                      customButton.variant === 'primary'
                        ? 'bg-teal-600 text-white border-teal-600 hover:bg-teal-700'
                        : `${colors.surface} ${colors.text} ${colors.border} ${colors.interactiveHover}`
                    )}
                    title={customButton.title || ''}
                  >
                    <span className="text-inherit">
                      {config.getToolbarIcon(customButton.icon)}
                    </span>
                  </Link>
                ) : customButton.onClick ? (
                  <button
                    onClick={async () => {
                      try {
                        await customButton.onClick!();
                      } catch (error: any) {
                        console.error('Custom button error:', error);
                        showError(error?.message || 'An error occurred');
                      }
                    }}
                    className={cn(
                      'inline-flex items-center justify-center h-9 w-9 rounded-lg border transition-colors',
                      customButton.variant === 'primary'
                        ? 'bg-teal-600 text-white border-teal-600 hover:bg-teal-700'
                        : `${colors.surface} ${colors.text} ${colors.border} ${colors.interactiveHover}`
                    )}
                    title={customButton.title || ''}
                  >
                    <span className="text-inherit">
                      {config.getToolbarIcon(customButton.icon)}
                    </span>
                  </button>
                ) : null}
              </div>
            );
          })()}

          {/* View Toggle - Icon button with dropdown (like Export) */}
          {config.toolbar.viewToggle?.enabled && (() => {
            const viewToggle = config.toolbar.viewToggle!;
            return (
              <div key="view-toggle" className="relative flex-shrink-0 inline-block" ref={viewToggleDropdownRef}>
                <button
                  ref={viewToggleButtonRef}
                  onClick={() => {
                    if (!viewToggleDropdownOpen && viewToggleButtonRef.current) {
                      const rect = viewToggleButtonRef.current.getBoundingClientRect();
                      setViewToggleDropdownPosition({
                        top: rect.bottom + 4,
                        right: window.innerWidth - rect.right,
                      });
                      setViewToggleDropdownOpen(true);
                    } else {
                      setViewToggleDropdownOpen(false);
                      setViewToggleDropdownPosition(null);
                    }
                  }}
                  className={cn(
                    'inline-flex items-center justify-center h-9 min-w-[2.25rem] px-2.5 rounded-lg border transition-colors',
                    viewToggleDropdownOpen
                      ? 'bg-teal-600 text-white border-teal-600'
                      : `${colors.surface} ${colors.text} ${colors.border} ${colors.interactiveHover}`
                  )}
                  title="View Options"
                >
                  <svg className="w-5 h-5 text-inherit shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-3zM14 13a1 1 0 011-1h4a1 1 0 011 1v6a1 1 0 01-1 1h-4a1 1 0 01-1-1v-6z" />
                  </svg>
                  <ChevronDownIcon
                    className={cn(
                      'w-4 h-4 ml-1 transition-transform text-inherit',
                      viewToggleDropdownOpen ? 'rotate-180' : ''
                    )}
                  />
                </button>

                {viewToggleDropdownOpen && (
                  <>
                    <div className="fixed inset-0 z-[90]" onClick={() => {
                      setViewToggleDropdownOpen(false);
                      setViewToggleDropdownPosition(null);
                    }} />
                    <div 
                      ref={viewToggleMenuRef}
                      className={`fixed z-[100] min-w-[180px] rounded-lg shadow-xl border ${colors.surface} ${colors.border} overflow-hidden`}
                      style={viewToggleDropdownPosition ? {
                        top: `${viewToggleDropdownPosition.top}px`,
                        right: `${viewToggleDropdownPosition.right}px`,
                      } : undefined}
                    >
                    {viewToggle.views.map((view) => {
                      const viewIcon = config.getToolbarIcon(view.icon);
                      return (
                        <Link
                          key={view.key}
                          to={view.href}
                          onClick={() => {
                            setViewToggleDropdownOpen(false);
                            setViewToggleDropdownPosition(null);
                          }}
                          className={`w-full px-3 py-2 text-sm text-left ${colors.text} transition-colors flex items-center gap-2`}
                        >
                          <span className="text-inherit">
                            {viewIcon}
                          </span>
                          <span>{view.label}</span>
                        </Link>
                      );
                    })}
                    </div>
                  </>
                )}
              </div>
            );
          })()}

          {/* Actions with position beforeTablePresets (e.g. Calendar view) */}
          {config.toolbar.actions
            .filter((action) => action.position === 'beforeTablePresets')
            .map((action) => {
              const iconElement = config.getToolbarIcon(action.icon);
              const showTitle = action.showTitle !== false;
              if (action.href) {
                return (
                  <Link
                    key={action.key}
                    to={action.href}
                    className={cn(
                      'inline-flex items-center justify-center text-sm font-medium rounded-lg border transition-colors whitespace-nowrap h-9',
                      showTitle ? 'px-3.5 py-2' : 'w-9 p-0',
                      action.variant === 'primary'
                        ? 'bg-teal-600 text-white hover:bg-teal-700 border-teal-600'
                        : `${colors.surface} ${colors.text} ${colors.border} ${colors.interactiveHover}`,
                      action.className
                    )}
                    title={action.title}
                  >
                    <span className="text-inherit">
                      {iconElement}
                    </span>
                    {showTitle && action.title}
                  </Link>
                );
              }
              return (
                <button
                  key={action.key}
                  onClick={action.onClick}
                  className={cn(
                    'inline-flex items-center gap-2 text-sm font-medium rounded-lg border transition-colors whitespace-nowrap h-9',
                    showTitle ? 'px-3.5 py-2' : 'w-9 justify-center p-0',
                    action.variant === 'primary'
                      ? 'bg-teal-600 text-white hover:bg-teal-700 border-teal-600'
                      : `${colors.surface} ${colors.text} ${colors.border} ${colors.interactiveHover}`,
                    action.className
                  )}
                  title={action.title}
                >
                  <span className="text-inherit">
                    {iconElement}
                  </span>
                  {showTitle && action.title}
                </button>
              );
            })}

          {/* Table Presets and Formatting Group */}
          <div className="flex items-center gap-1 flex-nowrap flex-shrink-0">
            {/* Table Presets Dropdown Button - Only show if enabled */}
            {enableTablePresets && (
            <div className="relative flex-shrink-0 inline-block" ref={tablePresetsDropdownRef}>
            <button
              ref={tablePresetsButtonRef}
              onClick={() => {
                // Open the full TablePresets panel directly for better UX
                setShowTablePresets(true);
                // Ensure any small dropdown is closed/reset
                setTablePresetsDropdownOpen(false);
                setTablePresetsDropdownPosition(null);
              }}
              className={cn(
                'inline-flex items-center justify-center h-9 w-9 rounded-lg border transition-colors',
                showTablePresets || tablePresetsDropdownOpen
                  ? 'bg-teal-600 text-white border-teal-600'
                  : `${colors.surface} ${colors.text} ${colors.border} ${colors.interactiveHover}`
              )}
              title="Table presets and customization"
            >
              <svg className="w-5 h-5 text-inherit" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.827 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.827 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.827-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.827-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>

            {tablePresetsDropdownOpen && (
              <>
                <div className="fixed inset-0 z-[90]" onClick={() => {
                  setTablePresetsDropdownOpen(false);
                  setTablePresetsDropdownPosition(null);
                }} />
                <div 
                  ref={tablePresetsMenuRef}
                  className={`fixed z-[100] min-w-[280px] rounded-lg shadow-xl border ${colors.border} ${colors.surface} overflow-hidden`}
                  style={tablePresetsDropdownPosition ? {
                    top: `${tablePresetsDropdownPosition.top}px`,
                    right: `${tablePresetsDropdownPosition.right}px`,
                  } : undefined}
                >
                {/* Clear/Default Option */}
                <button
                  onClick={() => handleApplyTablePreset('')}
                  className={cn(
                    'w-full px-4 py-2.5 text-sm text-left transition-colors flex items-center justify-between',
                    savedTablePresets.length === 0 || !savedTablePresets.find((p) => isTablePresetActive(p)) 
                      ? `${theme === 'dark' ? 'bg-teal-900/30' : 'bg-teal-50'} border-l-2 border-teal-600` 
                      : ''
                  )}
                >
                  <div className="flex items-center gap-2.5">
                    <XIcon className={cn('w-4 h-4', savedTablePresets.length === 0 || !savedTablePresets.find((p) => isTablePresetActive(p)) ? 'text-teal-600' : colors.textMuted)} />
                    <span className={cn('font-medium', savedTablePresets.length === 0 || !savedTablePresets.find((p) => isTablePresetActive(p)) ? 'text-teal-600 dark:text-teal-400' : colors.text)}>Default View</span>
                  </div>
                  {(savedTablePresets.length === 0 || !savedTablePresets.find((p) => isTablePresetActive(p))) && (
                    <CheckIcon className={`w-4 h-4 ${colors.primaryText}`} />
                  )}
                </button>

                {savedTablePresets.length > 0 && <div className={`border-t ${colors.border}`} />}

                {/* Saved Presets */}
                {savedTablePresets.map((preset: any) => {
                  const isActive = isTablePresetActive(preset);
                  const isDefault = preset.isDefault;

                  return (
                    <button
                      key={preset.id}
                      onClick={() => handleApplyTablePreset(preset.id.toString())}
                      className={cn(
                        'w-full px-4 py-2.5 text-sm text-left transition-colors',
                        isActive 
                          ? `${theme === 'dark' ? 'bg-teal-900/30' : 'bg-teal-50'} border-l-2 border-teal-600` 
                          : ''
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2.5 flex-1 min-w-0">
                          <svg className={cn('w-4 h-4 flex-shrink-0', isActive ? colors.primaryText : colors.textSecondary)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.827 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.827 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.827-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.827-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <div className="flex flex-col flex-1 min-w-0">
                            <span className={cn('text-sm font-medium truncate', isActive ? 'text-teal-700 dark:text-teal-300' : colors.text)}>{preset.name}</span>
                            <span className={`text-xs ${colors.textMuted}`}>
                              {preset.description || 'Table layout preset'}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5">
                          {isDefault && (
                            <span className={`px-2 py-0.5 text-xs font-medium ${colors.success} rounded-full`}>
                              Default
                            </span>
                          )}
                          {isActive && <CheckIcon className={`w-4 h-4 ${colors.primaryText}`} />}
                        </div>
                      </div>
                    </button>
                  );
                })}

                {/* Manage Presets Link */}
                <div className={`border-t ${colors.border}`} />
                <button
                  onClick={() => {
                    setTablePresetsDropdownOpen(false);
                    setShowTablePresets(true);
                  }}
                  className={`w-full px-4 py-2.5 text-sm text-left transition-colors ${colors.primaryText} font-medium`}
                >
                  <div className="flex items-center gap-2.5">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.827 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.827 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.827-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.827-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span>Manage Presets...</span>
                  </div>
                </button>
                </div>
              </>
            )}

            </div>
            )}

            {/* Conditional Formatting Button - Only show if enabled */}
            {enableConditionalFormatting && (
            <div className="relative flex-shrink-0 inline-block" ref={conditionalFormattingRef}>
              <button
                ref={conditionalFormattingButtonRef}
                onClick={() => {
                  if (!showConditionalFormatting && conditionalFormattingButtonRef.current) {
                    const rect = conditionalFormattingButtonRef.current.getBoundingClientRect();
                    setConditionalFormattingPosition({
                      top: rect.bottom + 8,
                      right: window.innerWidth - rect.right,
                    });
                    setShowConditionalFormatting(true);
                  } else {
                    setShowConditionalFormatting(false);
                    setConditionalFormattingPosition(null);
                  }
                }}
                className={cn(
                  'inline-flex items-center justify-center h-9 w-9 rounded-lg border transition-colors',
                  showConditionalFormatting
                    ? 'bg-teal-600 text-white border-teal-600'
                    : `${colors.surface} ${colors.text} ${colors.border} ${colors.interactiveHover}`
                )}
                title="Conditional Formatting"
              >
                <PaintBrushIcon className="w-5 h-5 text-inherit" />
              </button>

              <ConditionalFormattingManager
                moduleName={moduleNameForConditionalFormatting}
                entityName={config.module.title}
                availableColumns={availableColumns}
                tableData={data || []}
                isOpen={showConditionalFormatting}
                onClose={() => {
                  setShowConditionalFormatting(false);
                  setConditionalFormattingPosition(null);
                }}
                onRulesChange={() => {
                  // Rules will be automatically reloaded via SWR
                }}
                position={conditionalFormattingPosition}
              />
            </div>
            )}
          </div>

          {config.toolbar.actions
            .filter((action) => {
              // Don't render here if rendered before table presets
              if (action.position === 'beforeTablePresets') {
                return false;
              }
              // Filter export/download actions based on enableExport feature flag
              if (action.key === 'download' && !enableExport) {
                return false;
              }
              // Hide "Add new" / create action when user has no edit permission
              if (permissionModuleKey && !canEdit && action.href === config.module.route.new) {
                return false;
              }
              return true;
            })
            .map((action) => {
            const iconElement = config.getToolbarIcon(action.icon);
            const showTitle = action.showTitle !== false; // Default to true unless explicitly set to false
            const hasDropdown = action.dropdown && action.dropdown.length > 0;
            const isDownloadDropdown = action.key === 'download' && hasDropdown;
            const dropdownOpen = isDownloadDropdown ? downloadDropdownOpen : false;
            const setDropdownOpen = isDownloadDropdown ? (open: boolean) => {
              if (open && isDownloadDropdown && downloadButtonRef.current) {
                const rect = downloadButtonRef.current.getBoundingClientRect();
                setDownloadDropdownPosition({
                  top: rect.bottom + 4,
                  right: window.innerWidth - rect.right,
                });
                setDownloadDropdownOpen(true);
              } else {
                setDownloadDropdownOpen(false);
                setDownloadDropdownPosition(null);
              }
            } : () => {};
            const dropdownRef = isDownloadDropdown ? downloadDropdownRef : null;
            const buttonRef = isDownloadDropdown ? downloadButtonRef : null;

            // Render dropdown button
            if (hasDropdown) {
              return (
                <div key={action.key} className="relative flex-shrink-0" ref={dropdownRef}>
                  <button
                    ref={buttonRef}
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    className={cn(
                      'inline-flex items-center gap-2 text-sm font-medium rounded-lg border transition-colors whitespace-nowrap h-9',
                      showTitle ? 'px-3.5 py-2' : 'w-9 justify-center p-0',
                      dropdownOpen
                        ? action.variant === 'primary'
                          ? 'bg-teal-700 text-white border-teal-700'
                          : `${colors.surfaceHover} ${colors.text} ${colors.border}`
                        : action.variant === 'primary'
                        ? 'bg-teal-600 text-white hover:bg-teal-700 border-teal-600'
                        : `${colors.surface} ${colors.text} ${colors.border} ${colors.interactiveHover}`,
                      action.className
                    )}
                    title={action.title}
                  >
                    <span className="text-inherit">
                      {iconElement}
                    </span>
                    {showTitle && action.title}
                    {hasDropdown && (
                      <ChevronDownIcon
                        className={cn(
                          'w-4 h-4 transition-transform text-inherit',
                          dropdownOpen ? 'rotate-180' : ''
                        )}
                      />
                    )}
                  </button>

                  {dropdownOpen && (
                    <>
                      <div className="fixed inset-0 z-[90]" onClick={() => setDropdownOpen(false)} />
                      <div 
                        ref={isDownloadDropdown ? downloadMenuRef : null}
                        className={`fixed z-[100] min-w-[180px] max-w-[240px] rounded-lg shadow-xl border ${colors.border} ${colors.surface} overflow-hidden`}
                        style={isDownloadDropdown && downloadDropdownPosition ? {
                          top: `${downloadDropdownPosition.top}px`,
                          right: `${downloadDropdownPosition.right}px`,
                        } : undefined}
                      >
                        {action.dropdown?.map((item) => {
                          const itemIcon = item.icon ? config.getToolbarIcon(item.icon) : null;
                          return (
                            <button
                              key={item.key}
                              onClick={async () => {
                                try {
                                  // Pass data context to onClick handler if it's a function that accepts parameters
                                  if (typeof item.onClick === 'function') {
                                    // Check if onClick expects data parameters
                                    if (item.onClick.length > 0) {
                                      await item.onClick({
                                        sortedData: sortedItems,
                                        tableColumns: allColumns,
                                        entityName: config.module.title,
                                        exportFileName: config.module.title.toLowerCase().replace(/\s+/g, '_'),
                                        formatDate: config.utils.formatDate,
                                      });
                                      // Don't show success for exports - file download indicates success
                                    } else {
                                      await item.onClick();
                                    }
                                  }
                                  setDropdownOpen(false);
                                } catch (error: any) {
                                  console.error('Action error:', error);
                                  const errorMessage = error?.message || 'An error occurred';
                                  showError(errorMessage);
                                  setDropdownOpen(false);
                                }
                              }}
                            className={`w-full px-3 py-2 text-sm text-left ${colors.text} transition-colors flex items-center gap-2`}
                          >
                            {itemIcon && (
                              <span className="text-inherit">
                                {itemIcon}
                              </span>
                            )}
                            <span>{item.label}</span>
                          </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              );
            }

            // Render regular button or link
            if (action.href) {
              return (
                <Link
                  key={action.key}
                  to={action.href}
                  className={cn(
                    'inline-flex items-center justify-center text-sm font-medium rounded-lg border transition-colors whitespace-nowrap h-9',
                    showTitle ? 'px-3.5 py-2' : 'w-9 p-0',
                    action.variant === 'primary'
                      ? 'bg-teal-600 text-white hover:bg-teal-700 border-teal-600'
                      : `${colors.surface} ${colors.text} ${colors.border} ${colors.interactiveHover}`,
                    action.className
                  )}
                  title={action.title}
                >
                  <span className="text-inherit">
                    {iconElement}
                  </span>
                  {showTitle && action.title}
                </Link>
              );
            }
            return (
              <button
                key={action.key}
                onClick={action.onClick}
                className={cn(
                  'inline-flex items-center gap-2 text-sm font-medium rounded-lg border transition-colors whitespace-nowrap h-9',
                  showTitle ? 'px-3.5 py-2' : 'w-9 justify-center p-0',
                  action.variant === 'primary'
                    ? 'bg-teal-600 text-white hover:bg-teal-700 border-teal-600'
                    : `${colors.surface} ${colors.text} ${colors.border} ${colors.interactiveHover}`,
                  action.className
                )}
                title={action.title}
              >
                <span className="text-inherit">
                  {iconElement}
                </span>
                {showTitle && action.title}
              </button>
            );
          })}
        </div>
      </div>

      {/* Quick Filter Buttons - Display saved filter presets as quick filters - Only show if advanced search is enabled */}
      {enableAdvancedSearch && (() => {
        const quickFilterPresets = savedFilterPresets.filter((preset: FilterPreset) => preset.showInQuickFilters);
        if (quickFilterPresets.length === 0) return null;

        // Always add "All" as the first filter when there are any quick filter presets
        const quickFilters = [
          {
            id: 'all',
            label: 'All',
            description: 'Show all items',
            filters: {},
            variant: 'default' as const,
          },
          ...quickFilterPresets.map((preset: FilterPreset) => ({
            id: preset.id.toString(),
            label: preset.name,
            description: preset.description,
            filters: preset.filters,
            variant: preset.isDefault ? ('primary' as const) : ('default' as const),
          })),
        ];

        return (
          <div className="flex-shrink-0 mb-1">
            <QuickFilterButtons
              quickFilters={quickFilters}
              currentFilters={unifiedFilters}
              onApplyFilters={(filters) => {
                setUnifiedFilters(() => ({ ...filters }));
              }}
              onQuickFilterSelect={(quickFilterId) => {
                setSearchParams((prev) => {
                  const next = new URLSearchParams(prev);
                  if (quickFilterId === 'all') {
                    next.delete('preset');
                  } else {
                    next.set('preset', quickFilterId);
                  }
                  return next;
                });
              }}
              layout="pills"
            />
          </div>
        );
      })()}

      {/* Filter Panels */}
      <div className="flex-shrink-0 mb-1">
        {filterSection?.filterPanels}
      </div>

      {/* Data Table - Flexible container */}
      <div className="flex-1 min-h-0 flex flex-col">
        <div className="flex-1 min-h-0 overflow-hidden">
          <DataTable
            data={paginatedItems}
            columns={columns}
            loading={isLoading}
            pagination={paginationProps}
            sorting={{
              sortConfig,
              onSort: handleSort,
            }}
            actions={config.table.actionsInColumns ? undefined : (item) => {
              const onDelete = (id: number) => setDeleteConfirm(id);
              const contextWithMutate = { ...listContext, mutate };
              let actionsContent: React.ReactNode;
              try {
                actionsContent = (config.getTableActions as any)(item, onDelete, colors, contextWithMutate, tableActionsOptions);
              } catch {
                try {
                  actionsContent = (config.getTableActions as any)(item, onDelete, colors, tableActionsOptions);
                } catch {
                  try {
                    actionsContent = (config.getTableActions as any)(item, onDelete, tableActionsOptions);
                  } catch {
                    actionsContent = (config.getTableActions as any)(item, onDelete, undefined, undefined, tableActionsOptions);
                  }
                }
              }
              const getClientId = (config.table as { getClientId?: (item: T) => number | null | undefined }).getClientId;
              const clientId = getClientId?.(item);
              if (typeof clientId === 'number') {
                return (
                  <>
                    <Link
                      to={`/clients/${clientId}/summary`}
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center justify-center p-1 rounded-md text-amber-600 hover:text-amber-900 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-900/30 transition-colors"
                      title="Client summary"
                      aria-label="Client summary"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </Link>
                    {actionsContent}
                  </>
                );
              }
              return actionsContent;
            }}
            actionsPosition={config.table.actionsPosition}
            emptyMessage={
              searchTerm
                ? config.emptyStates.noSearchResults
                : config.emptyStates.noData
            }
            onRowClick={config.table.onRowClick}
            keyExtractor={config.table.keyExtractor}
            density={tableDensity}
            conditionalFormattingRules={enableConditionalFormatting ? conditionalFormattingRules : []}
            showFooter={footerConfig?.showFooter || false}
            footerData={(footerConfig?.useFilteredData ?? true) ? sortedItems : data} // Use filtered data if enabled, otherwise all data
          />
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <ConfirmationDialog
          isOpen={!!deleteConfirm}
          onClose={() => setDeleteConfirm(null)}
          onConfirm={() => handleDelete(deleteConfirm)}
          title={config.delete.title}
          message={config.delete.message}
          confirmText={config.delete.confirmText}
          cancelText={config.delete.cancelText}
          confirmVariant="danger"
        />
      )}
    </div>
    </div>
    {enableTablePresets && showTablePresets && (
      <div
        className={cn('flex-shrink-0 flex flex-col min-h-0 relative', colors.border)}
        style={{ width: tablePresetsPanelWidth }}
      >
        {/* Resize handle (sizebar) on the left edge */}
        <div
          data-table-presets-resize
          role="separator"
          aria-orientation="vertical"
          aria-valuenow={tablePresetsPanelWidth}
          onMouseDown={(e) => {
            if (e.button !== 0) return;
            tablePresetsResizeStartRef.current = { x: e.clientX, width: tablePresetsPanelWidth };
            setTablePresetsResizing(true);
          }}
          className={cn(
            'absolute left-0 top-0 bottom-0 w-2 -ml-px z-10 cursor-col-resize flex items-center justify-center group',
            'hover:bg-teal-500/20 active:bg-teal-500/30',
            tablePresetsResizing && 'bg-teal-500/30'
          )}
          title="Drag to resize panel"
        >
          <div className="w-0.5 h-12 rounded-full bg-gray-400 dark:bg-gray-500 opacity-60 group-hover:opacity-100" />
        </div>
        <div className={cn('flex flex-col flex-1 min-h-0 border-l', colors.border)}>
        <TablePresets
          visibleColumns={visibleColumns}
          onApplyVisibleColumns={setVisibleColumns}
          allColumns={allColumns.map((col) => ({ key: String(col.key), header: col.header }))}
          tableDensity={tableDensity}
          onApplyDensity={setTableDensity}
          columnWidths={columnWidths}
          columnOrder={columnOrder}
          columnLabels={columnLabels}
          onApplyColumnLabels={setColumnLabels}
          sortConfig={sortConfig ? [{ key: sortConfig.key, direction: sortConfig.direction, priority: 1 }] : []}
          onApplyTableConfig={(tableConfig) => {
            if (tableConfig.columnWidths) setColumnWidths(tableConfig.columnWidths);
            if (tableConfig.columnOrder) setColumnOrder(tableConfig.columnOrder);
            if (tableConfig.sortConfig && tableConfig.sortConfig.length > 0) {
              const first = tableConfig.sortConfig[0];
              setSortConfig({ key: first.key, direction: first.direction });
            } else setSortConfig(null);
          }}
          footerConfig={footerConfig}
          onApplyFooterConfig={setFooterConfig}
          tableId={tableId}
          entityName={config.module.title}
          isOpen
          asPanel
          lastAppliedPreset={lastAppliedPreset}
          onPresetApplied={setLastAppliedPreset}
          onPresetCleared={() => setLastAppliedPreset(null)}
          onClose={() => setShowTablePresets(false)}
          onLoadDefaultPreset={handleLoadDefaultPreset}
          onPresetsChange={() => mutateTablePresets()}
        />
        </div>
      </div>
    )}
    </div>
  );
}

export default GenericListPage;

